import { useEffect, useRef, useState } from 'react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

import { Text, useSitecoreContext } from '@sitecore-jss/sitecore-jss-nextjs';

import { SitecoreContextValue } from 'src/lib/ComponentProps';

import { downloadPages } from 'src/services/downloadPage/downloadPages';

import { getContentTitle, getMultipleResourcesUrls } from 'src/utils/getResourcesUrls';
import IsSitecoreEditing from 'src/utils/isSitecoreEditing';

import { Button } from 'src/components/basicContent/_Button/Button';
import { AllResourceUrls } from 'src/components/contentOverview/contentOverviewToolbar/ContentOverviewToolbarFields';

import CloseIconSvg from 'public/icons/close.svg';

import styles from './DownloadOptions.module.css';

type DownloadOptionsProps = {
	showDownloadOptions: boolean;
	allResourceUrls?: AllResourceUrls[];
	currentResourceUrl?: string;
	hideDownloadOptions: (arg0: boolean) => void;
};

export default function DownloadOptions(props: DownloadOptionsProps): JSX.Element {
	const { sitecoreContext } = useSitecoreContext<SitecoreContextValue>();
	const isEditing = IsSitecoreEditing();
	const contentTitle = getContentTitle(props.currentResourceUrl);
	const isMounted = useRef(true);

	const [loaded, setLoaded] = useState(false);
	const [singlePDFDownloading, setSinglePDFDownloading] = useState(false);
	const [fullPDFDownloading, setFullPDFDownloading] = useState(false);
	const [singlePDFHtml, setSinglePDFHtml] = useState('');
	const [fullPDFHtml, setFullPDFHtml] = useState('');

	const singlePDFContainer = useRef<HTMLDivElement>(null);
	const fullPDFContainer = useRef<HTMLDivElement>(null);

	useEffect(() => {
		const handleLoad = () => setLoaded(true);

		if (document.readyState === 'complete') {
			handleLoad();
		} else {
			window.addEventListener('load', handleLoad);
		}

		return () => {
			window.removeEventListener('load', handleLoad);
		};
	}, []);

	useEffect(() => {
		isMounted.current = true;
		return () => {
			isMounted.current = false;
		};
	}, []);

	// Reset loading state after download
	useEffect(() => {
		if (singlePDFDownloading || fullPDFDownloading) {
			const timer = setTimeout(() => {
				setSinglePDFDownloading(false);
				setFullPDFDownloading(false);
			}, 5000);
			return () => clearTimeout(timer);
		}
		return;
	}, [singlePDFDownloading, fullPDFDownloading]);

	const handlePDFDownload = async (urls: string[], target: 'singlePDF' | 'fullPDF') => {
		await downloadPages({
			urls,
			onPDFReady: (html: string) => {
				if (!isMounted.current) {
					return;
				}
				const cleaned = cleanHTML(html);
				if (target === 'singlePDF') {
					setSinglePDFHtml(cleaned);
				} else {
					setFullPDFHtml(cleaned);
				}
			},
		});
	};

	const cleanHTML = (html: string): string => {
		const parser = new DOMParser();
		const doc = parser.parseFromString(html, 'text/html');
		const fragment = document.createElement('div');

		// REMOVE ALL *LIVE PAGE* HEADING IDS
		doc.querySelectorAll('[data-section-header]').forEach((el) => {
			el.removeAttribute('id');
		});

		// Extract only the PDF content
		doc.querySelectorAll('[data-display-pdf-download]').forEach((el) => {
			fragment.appendChild(el.cloneNode(true));
		});

		return fragment.outerHTML;
	};

	const generatePdfFromHtml = async (htmlElement: HTMLElement | null) => {
		if (!htmlElement) {
			return;
		}

		const images = htmlElement.querySelectorAll('img');
		const publicUrl = process.env.PUBLIC_URL;

		images.forEach((img) => {
			img.setAttribute('crossorigin', 'anonymous');

			if (publicUrl) {
				const url = new URL(img.src);
				const path = url.pathname + url.search;
				img.src = `${publicUrl}${path}`;
			}
		});

		return html2canvas(htmlElement, { useCORS: true, scale: 1.5, allowTaint: false, logging: false }).then((canvas) => {
			const imgWidth = 190;
			const pageHeight = 297;
			// Set different bottom margin based on screen resolution
			const bottomMargin = window.innerWidth < 500 ? 40 : 20;

			const imgHeight = (canvas.height * imgWidth) / canvas.width;

			let heightLeft = imgHeight;
			let position = 0;
			let pageNumber = 1;
			const totalPages = Math.ceil(heightLeft / (pageHeight - bottomMargin));

			const pdf = new jsPDF();

			while (heightLeft > 0) {
				// Calculate remaining page height, excluding bottom margin for content only
				const pageContentHeight = pageHeight - bottomMargin;

				// Slice the content for the current page
				const canvasPage = canvas
					.getContext('2d')
					?.getImageData(0, position, canvas.width, Math.min(canvas.height, pageContentHeight * (canvas.width / imgWidth)));
				if (!canvasPage) {
					continue;
				}

				const pageCanvas = document.createElement('canvas');
				pageCanvas.width = canvas.width;
				pageCanvas.height = Math.min(canvas.height - position, pageContentHeight * (canvas.width / imgWidth));
				pageCanvas.getContext('2d')?.putImageData(canvasPage, 0, 0);
				const imgData = pageCanvas.toDataURL('image/png');

				if (position === 0) {
					pdf.addImage(imgData, 'PNG', 10, 5, imgWidth, pageCanvas.height * (imgWidth / canvas.width));
				} else {
					pdf.addPage();
					pdf.setFontSize(10);
					pdf.addImage(imgData, 'PNG', 10, 25, imgWidth, pageCanvas.height * (imgWidth / canvas.width));
				}

				if (totalPages > 1) {
					const footerText = `Page ${pageNumber}`;
					const pageWidth = pdf.internal.pageSize.getWidth();
					const footerX = pageWidth - pdf.getTextWidth(footerText) - 10;
					pdf.setFontSize(10);
					pdf.text(footerText, footerX, pageHeight - 10);
				}

				heightLeft -= pageContentHeight;
				position += pageContentHeight * (canvas.width / imgWidth);
				pageNumber++;
			}

			pdf.save(`${contentTitle}.pdf`);
		});
	};

	const handleDownload = async ({
		container,
		urls,
		target,
		setDownloading,
	}: {
		container: React.RefObject<HTMLDivElement>;
		urls: string[];
		target: 'singlePDF' | 'fullPDF';
		setDownloading: (value: boolean) => void;
	}) => {
		if (!container.current || !urls.length) {
			return;
		}

		try {
			setDownloading(true);

			await handlePDFDownload(urls, target);

			await new Promise((resolve) => requestAnimationFrame(resolve));

			await generatePdfFromHtml(container.current);
		} catch (error) {
			console.error('PDF generation failed', error);
			throw error;
		} finally {
			setDownloading(false);
		}
	};

	return (
		<div className={`${styles.downloadOptionsModal} ${props.showDownloadOptions ? styles.active : ''}`} id="download-options-modal">
			<div className={styles.downloadOptionsModalContainer}>
				<Button title="Close" className={styles.closeModalButton} onClick={() => props.hideDownloadOptions(false)}>
					<CloseIconSvg />
				</Button>

				{/* Download Single Page */}
				<Button
					title="download-single-page-pdf"
					className={styles.downloadOptionsButton}
					onClick={async () => {
						if (!props.currentResourceUrl || isEditing) {
							return;
						}

						try {
							await handleDownload({
								container: singlePDFContainer,
								urls: [props.currentResourceUrl],
								target: 'singlePDF',
								setDownloading: setSinglePDFDownloading,
							});
						} catch (error) {
							console.error('Download failed', error);
						}
					}}
					disabled={!loaded || singlePDFDownloading}
				>
					<>
						<Text
							className={styles.downloadOptionsButtonText}
							field={{
								value: singlePDFDownloading ? sitecoreContext.dictionary?.downloadingPDF : sitecoreContext.dictionary?.downloadSinglePage,
							}}
						/>
						{(!loaded || singlePDFDownloading) && <span className={styles.loader} />}
					</>
				</Button>

				{/* Download Full Resource */}
				{props.allResourceUrls && props.allResourceUrls.length > 1 && (
					<Button
						title="download-full-resource-pdf"
						className={styles.downloadOptionsButton}
						onClick={async () => {
							if (!props.allResourceUrls || props.allResourceUrls.length <= 1 || isEditing) {
								return;
							}

							const urls = getMultipleResourcesUrls(props.allResourceUrls);
							if (!urls.length) {
								return;
							}

							try {
								await handleDownload({
									container: fullPDFContainer,
									urls,
									target: 'fullPDF',
									setDownloading: setFullPDFDownloading,
								});
							} catch (error) {
								console.error('Full PDF download failed', error);
								// optional: show toast or UI error
							}
						}}
						disabled={!loaded || fullPDFDownloading}
					>
						<>
							<Text
								className={styles.downloadOptionsButtonText}
								field={{
									value: fullPDFDownloading ? sitecoreContext.dictionary?.downloadingPDF : sitecoreContext.dictionary?.downloadFullResource,
								}}
							/>
							{(!loaded || fullPDFDownloading) && <span className={styles.loader} />}
						</>
					</Button>
				)}
			</div>

			<div className={styles.HidePDF}>
				<div ref={singlePDFContainer} className={styles.printWrapper} dangerouslySetInnerHTML={{ __html: singlePDFHtml }} />
			</div>
			<div className={styles.HidePDF}>
				<div ref={fullPDFContainer} className={styles.printWrapper} dangerouslySetInnerHTML={{ __html: fullPDFHtml }} />
			</div>
		</div>
	);
}
